import { useLazyQuery, useQuery } from '@apollo/client';
import { GET_PROTECTION_PLANS_BY_USER_ID } from '../../../services/protectionPlanService/queries';
import {
  IGetProtectionPlansByUserIdQuery,
  IGetProtectionPlansByUserIdQueryVariables,
} from '../../../services/protectionPlanService/__generated__/queries.generated';
import { IGetCarQuery } from '../../../services/carService/__generated__/queries.generated';
import { GET_CAR } from '../../../services/carService/queries';
import { carAsRented } from '../../../utils/carTripSettings';
import { useParams } from 'react-router-dom';
import { IProtectionPlan, ProtectionPlanEnum } from '../../../../__generated__/graphql';
import { useState, useEffect } from 'react';
import BaseDialog from '../../dialog';
import {
  formatDeclinedLiabilityPercent,
  resolveDeclinedProtectionOutOfPocketPercent,
} from '../../../utils/declinedProtectionLiability';
import { PlanRadioList } from '../planRadioList';
import { ProtectionPlanCoverageDetails } from '../protectionPlanCoverageDetails';

function isSelectablePaidPlan(plan: { plan: ProtectionPlanEnum; isActive?: boolean | null }) {
  return plan.plan !== ProtectionPlanEnum.Declined && plan.isActive !== false;
}

function isMonthlyPlan(plan: string) {
  return plan.startsWith('MONTHLY_');
}

function isPlanVisibleForRentalType(
  plan: { plan: ProtectionPlanEnum; isActive?: boolean | null },
  rentalType?: string | null,
  hasActiveMonthlyPlans = false,
) {
  if (!isSelectablePaidPlan(plan)) return false;
  const isMonthlyBooking = rentalType === 'MONTHLY';
  if (isMonthlyBooking && hasActiveMonthlyPlans) return isMonthlyPlan(plan.plan);
  return !isMonthlyPlan(plan.plan);
}

export const ChooseProtectionPlan = ({
  onSelectedPlan,
  rentalType,
}: {
  onSelectedPlan: (plan: IProtectionPlan) => void;
  rentalType?: string | null;
}) => {
  const [selected, setSelected] = useState<IProtectionPlan | null>(null);
  const { id: carId } = useParams<{ id: string }>();
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [
    getProtectionPlansFromUser,
    { data: protectionPlansData, loading: protectionPlansLoading },
  ] = useLazyQuery<IGetProtectionPlansByUserIdQuery, IGetProtectionPlansByUserIdQueryVariables>(
    GET_PROTECTION_PLANS_BY_USER_ID,
  );
  const {
    data: carData,
    loading: carLoading,
    error: carError,
  } = useQuery<IGetCarQuery>(GET_CAR, {
    fetchPolicy: 'cache-first',
    variables: { id: carId },
    onCompleted: data => {
      if (data?.car?.owner?.id) {
        getProtectionPlansFromUser({
          variables: { userId: data?.car?.owner?.id || '' },
        });
      } else {
        throw new Error('Car owner not found');
      }
    },
  });
  const getDeclineDescription = () => {
    const declined = protectionPlansData?.protectionPlansByUserId?.find(
      plan => plan.plan === ProtectionPlanEnum.Declined,
    );
    if (!declined) return '';

    const liability = formatDeclinedLiabilityPercent(
      resolveDeclinedProtectionOutOfPocketPercent({
        percent: (declined as any).declinedProtectionOutOfPocketPercent,
        legacyPolicy: (declined as any).declinedProtectionOutOfPocketAmt,
      }),
    );

    const parts = [`Maximum liability: ${liability}.`];
    const description = declined.description?.trim();
    if (description) {
      parts.push(description);
    }
    return parts.join('\n\n');
  };

  const hasActiveMonthlyPlans = !!protectionPlansData?.protectionPlansByUserId?.some(
    p => isMonthlyPlan(p.plan) && p.isActive !== false,
  );
  const declinedPlan = protectionPlansData?.protectionPlansByUserId?.find(
    plan => plan.plan === ProtectionPlanEnum.Declined,
  );
  // Spec 014b: the brand may decide for every car (`tripSettings`).
  const carAllowsDecline = carAsRented(carData?.car as any)?.isProtectionDeclinable !== false;
  const rentalTypeAllowsDecline =
    rentalType === 'MONTHLY'
      ? (declinedPlan as any)?.isMonthlyProtectionPlanDeclinable !== false
      : (declinedPlan as any)?.isDailyProtectionPlanDeclinable !== false;
  const canDeclineProtection =
    !!declinedPlan &&
    declinedPlan.isActive !== false &&
    carAllowsDecline &&
    rentalTypeAllowsDecline;

  useEffect(() => {
    if (!protectionPlansData?.protectionPlansByUserId) return;

    const candidates = protectionPlansData.protectionPlansByUserId.filter(plan =>
      isPlanVisibleForRentalType(plan, rentalType, hasActiveMonthlyPlans),
    );

    const selectedStillVisible =
      (selected?.plan === ProtectionPlanEnum.Declined && canDeclineProtection) ||
      (selected && isPlanVisibleForRentalType(selected, rentalType, hasActiveMonthlyPlans));

    if (!selectedStillVisible) {
      const defaultPlan = candidates[0];
      if (defaultPlan) {
        setSelected(defaultPlan as IProtectionPlan);
      } else if (selected) {
        setSelected(null);
      }
    }
  }, [protectionPlansData, rentalType, hasActiveMonthlyPlans, selected, canDeclineProtection]);
  useEffect(() => {
    if (selected) {
      onSelectedPlan(selected);
    }
  }, [selected]);
  return (
    <div>
      <BaseDialog
        isOpen={showDeclineDialog}
        onClose={() => {
          setShowDeclineDialog(false);
        }}
        title="Decline protection plan"
        description={getDeclineDescription()}
        confirmText="Decline"
        cancelText="Choose Minimum Coverage"
        onConfirm={() => {
          setShowDeclineDialog(false);
          const plan = protectionPlansData?.protectionPlansByUserId?.find(
            plan => plan.plan === ProtectionPlanEnum.Declined,
          );
          setSelected(plan as IProtectionPlan);
        }}
        onCancel={() => {
          setShowDeclineDialog(false);
          const isMonthlyBooking = rentalType === 'MONTHLY';
          const plans = protectionPlansData?.protectionPlansByUserId || [];
          const monthlyMinimum = plans.find(
            p => isSelectablePaidPlan(p) && p.plan === ProtectionPlanEnum.MonthlyMinimum,
          );
          const dailyMinimum = plans.find(
            p => isSelectablePaidPlan(p) && p.plan === ProtectionPlanEnum.Minimum,
          );
          const plan =
            isMonthlyBooking && hasActiveMonthlyPlans && monthlyMinimum
              ? monthlyMinimum
              : dailyMinimum;
          setSelected(plan as IProtectionPlan);
        }}
      />
      <PlanRadioList
        plans={(protectionPlansData?.protectionPlansByUserId || []) as IProtectionPlan[]}
        selected={selected}
        setSelected={setSelected}
        setShowDeclineDialog={setShowDeclineDialog}
        rentalType={rentalType}
        hasActiveMonthlyPlans={hasActiveMonthlyPlans}
        canDeclineProtection={canDeclineProtection}
      />
      {selected?.plan === ProtectionPlanEnum.Declined && (
        <ProtectionPlanCoverageDetails plan={selected} />
      )}
    </div>
  );
};
